
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import SelectField from '../../../framework/fields/SelectField.vue';
import SwitchField from '../../../framework/fields/SwitchField.vue';
import { InvoicePeriodizationType } from '../../../../common/application/model/enums/InvoicePeriodizationType';
import { InvoicePeriodizationDayOffset } from '../../../../common/application/model/enums/InvoicePeriodizationDayOffset';
import { getEnumerationOptions } from '../../../framework/service/options';
import { PeriodizationModel } from '../../../../common/application/model/PeriodizationModel';
import { InvoicePeriodizationOffsetDirection } from '../../../../common/application/model/enums/InvoicePeriodizationOffsetDirection';

@Component({ name: 'PeriodizationTypeSelector', components: { SelectField, SwitchField } })
export default class PeriodizationTypeSelector extends Vue {
    @Prop({ required: true }) readonly modelValue!: PeriodizationModel;

    @Prop({ default: false }) readonly readonly!: boolean;

    local = {
        invoicePeriodizationType: InvoicePeriodizationType.UNSPECIFIED,
        useOffset: false,
        direction: InvoicePeriodizationOffsetDirection.ADD,
        days: '2',
    };

    invoicePeriodizationTypeOptions: { id: string | undefined; label: string }[] = [];
    offsetDaysOptions: { id: string | undefined; label: string }[] = [];
    offsetDirectionOptions: { id: string | undefined; label: string }[] = [];

    mounted() {
        this.local = {
            invoicePeriodizationType: this.modelValue.type,
            useOffset: this.modelValue.useOffset,
            direction: this.modelValue.direction,
            days: String(this.modelValue.days ?? '2'),
        };

        this.invoicePeriodizationTypeOptions = getEnumerationOptions(
            'InvoicePeriodizationType',
            Object.keys(InvoicePeriodizationType),
            this,
        );

        this.offsetDaysOptions = getEnumerationOptions(
            'InvoicePeriodizationDayOffset',
            Object.values(InvoicePeriodizationDayOffset)
                .filter((v) => typeof v === 'number')
                .map(String),
            this,
        );

        this.offsetDirectionOptions = getEnumerationOptions(
            'InvoicePeriodizationOffsetDirection',
            Object.values(InvoicePeriodizationOffsetDirection),
            this,
        );
    }

    @Watch('modelValue', { deep: true, immediate: true })
    onModelValueChanged(newVal: PeriodizationModel) {
        if (!newVal) return;
        if (
            newVal.type === this.local.invoicePeriodizationType &&
            newVal.useOffset === this.local.useOffset &&
            newVal.direction === this.local.direction &&
            String(newVal.days ?? '2') === this.local.days
        ) {
            return;
        }

        this.local = {
            invoicePeriodizationType: newVal.type,
            useOffset: newVal.useOffset,
            direction: newVal.direction,
            days: String(newVal.days ?? '2'),
        };
    }

    @Watch('local', { deep: true })
    onLocalChange() {
        if (
            this.modelValue &&
            this.modelValue.type === this.local.invoicePeriodizationType &&
            this.modelValue.useOffset === this.local.useOffset &&
            this.modelValue.direction === this.local.direction &&
            String(this.modelValue.days ?? '2') === this.local.days
        ) {
            return;
        }

        this.$emit('update:modelValue', {
            type: this.local.invoicePeriodizationType,
            useOffset: this.local.useOffset,
            direction: this.local.direction,
            days: Number(this.local.days),
        });
    }

    get renderSummary(): string {
        const baseLabel =
            this.invoicePeriodizationTypeOptions.find((opt) => opt.id === this.modelValue.type)?.label || '';

        if (!this.modelValue.useOffset || !this.modelValue.days) return baseLabel;
        const directionLabel = this.$t(`enum.invoicePeriodizationOffsetDirection.${this.modelValue.direction}`);

        return `${baseLabel} - ${directionLabel} ${this.modelValue.days} ${this.$t('field.days')}`;
    }
}
